<template>
  <div class="wrapper">
    <table class="table">
      <thead class="header">
        <th v-for="header in headers" :key="header">
          <template v-if="header === checkboxColumn">
            <div class="checkbox-cell">
              <Checkbox
                @selected="onHeaderCheckboxSelected"
                @unselected="onHeaderCheckboxUnselected"
                :options="['check']"
                :show-label="false"
              />
            </div>
          </template>
          <template v-else>{{ header }}</template>
        </th>
      </thead>
      <tbody>
        <tr v-for="item in tableItems" class="row" :key="item.id">
          <td
            v-for="header in headers"
            :class="[
              ![statusColumn, clickableColumn].includes(header)
                ? getColumnClass(header, item)
                : '',
            ]"
            @click="columnAction(item, header)"
            :key="header"
          >
            <div class="checkbox-cell" v-if="header === checkboxColumn">
              <Checkbox
                :key="selectedCheckBoxes.length"
                @unselected="onCheckboxUnselected"
                @selected="onCheckboxSelected"
                :checked-values="selectedCheckBoxes"
                :options="[item[checkboxFieldToEmit]]"
                :show-label="false"
              />
            </div>
            <div class="drop-down-list" v-else-if="header === dropdownColumn">
              <select
                class="dropdownColumn"
                name="option"
                id="option"
                @change="$emit('dropdownSelect', [$event.target?.value, item])"
              >
                <option value="" selected>{{ dropdownText }}</option>
                <option
                  v-for="(option, optionIdx) in dropdownOptions"
                  v-bind:value="option"
                  :key="optionIdx"
                >
                  {{ option }}
                </option>
              </select>
            </div>
            <div v-else-if="header === deleteColumn">
              <button class="deleteSVG" v-if="deleteVariant === 'black'">
                <img src="../../assets/trash-black.svg" alt="Delete" />
              </button>
              <button class="deleteSVG" v-else>
                <img src="../../assets/trash.svg" alt="Delete" />
              </button>
            </div>
            <div v-else-if="header === editColumn">
              <button class="editSVG">
                <img src="../../assets/edit.svg" alt="Edit" />
              </button>
            </div>
            <div v-else-if="header === radioToggleColumn">
              <RadioToggle
                :default-value="item[header]"
                :show-text="false"
                @selected="radioToggleSelected"
                :id="item[radioFieldToEmit]"
                :editable="radioEditable"
                :off-value="radioToggleOptions?.get('off-value')"
                :on-value="radioToggleOptions?.get('on-value')"
                :disabled-value="radioToggleOptions?.get('disabled-value')"
              />
            </div>
            <div v-else-if="header === copyColumn">
              <span>{{ item[header] }}</span>
              <img
                alt="copy"
                class="copy-icon"
                @click="copyCell"
                src="../../assets/copy-white.svg"
                v-if="copyVariant === 'white'"
              />
              <img
                alt="copy"
                class="copy-icon"
                @click="copyCell"
                src="../../assets/copy.svg"
                v-else
              />
            </div>
            <div v-else-if="header === cryptoColumn" class="crypto-column">
              <img
                :src="require(`./assets/${item[header]}.svg`)"
                alt="network"
              />
              {{ item[header] }}
            </div>
            <div v-else-if="header === statusColumn" class="status-container">
              <span :class="getColumnClass(header, item)">{{
                item[header]
              }}</span>
            </div>
            <div
              v-else-if="header === clickableColumn"
              class="clickable-container"
            >
              <span :class="getColumnClass(header, item)">{{
                item[header]
              }}</span>
            </div>
            <div v-else>
              {{ item[header] }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from 'vue';
import RadioToggle from '../RadioToggle/RadioToggle.vue';
import Checkbox from '../Checkbox/Checkbox.vue';

export default defineComponent({
  name: 'DataTable',
  props: {
    tableData: {
      type: Array,
      required: true,
    },
    columnsToDisplay: {
      type: Array,
      required: true,
    },
    clickableColumn: {
      type: String,
    },
    copyColumn: {
      type: String,
      required: false,
    },
    copyVariant: {
      type: String,
      required: false,
      default: 'black',
    },
    dropdownColumn: {
      type: String,
      required: false,
    },
    dropdownOptions: {
      type: Array,
      required: false,
    },
    dropdownText: {
      type: String,
      required: false,
    },
    statusColumn: {
      type: String,
    },
    deleteColumn: {
      type: String,
    },
    editColumn: {
      type: String,
    },
    deleteVariant: {
      type: String,
      required: false,
      default: 'white',
    },
    statusCategories: {
      type: Map<string, string>,
    },
    radioToggleColumn: {
      type: String,
      reqired: false,
    },
    radioToggleOptions: {
      type: Map<string, string>,
      required: false,
    },
    radioEditable: {
      type: Boolean,
      default: true,
    },
    radioFieldToEmit: {
      type: String,
      required: false,
      default: 'id',
    },
    checkboxFieldToEmit: {
      type: String,
      required: false,
      default: 'id',
    },
    cryptoColumn: {
      type: String,
      required: false,
      validator: (val: string) => ['USDC', 'USDT', 'ETH', 'BTC'].includes(val),
    },
    checkboxColumn: {
      type: String,
      required: false,
    },
    clickableDisabledField: {
      type: String,
      required: false,
    },
  },
  emits: [
    'viewRow',
    'dropdownSelect',
    'delete',
    'edit',
    'radioToggleSelected',
    'copy',
    'selectedCheckboxes',
  ],
  components: {
    RadioToggle,
    Checkbox,
  },
  setup(props, { emit }) {
    const tableItems = reactive<any[]>(props.tableData);
    const headers = props.columnsToDisplay as string[];
    const columnNumber = headers.length;
    const openDropdown = ref(false);
    const selectedCheckBoxes = ref<any[]>([]);

    const copyCell = (evt: any) => {
      const img = evt.target;
      const cell = img.parentNode;
      const span = cell.firstChild;
      emit('copy', span.textContent);
    };

    const columnAction = (item: any, header: string) => {
      switch (header) {
        case props.clickableColumn:
          // Check if the row is disabled for clickable column
          if (
            props.clickableDisabledField &&
            item[props.clickableDisabledField]
          ) {
            return;
          }
          emit('viewRow', item);
          break;
        case props.dropdownColumn:
          openDropdown.value = true;
          break;
        case props.deleteColumn:
          emit('delete', item);
          break;
        case props.editColumn:
          emit('edit', item);
          break;
        default:
          break;
      }
    };

    const emitSelectedCheckboxes = () => {
      emit('selectedCheckboxes', selectedCheckBoxes.value);
    };

    const onCheckboxSelected = (evt: any) => {
      selectedCheckBoxes.value.push(evt);
      emitSelectedCheckboxes();
    };

    const onCheckboxUnselected = (evt: any) => {
      selectedCheckBoxes.value = selectedCheckBoxes.value.filter(
        value => value !== evt,
      );
      emitSelectedCheckboxes();
    };

    const onHeaderCheckboxUnselected = () => {
      selectedCheckBoxes.value = [];
      emitSelectedCheckboxes();
    };

    const onHeaderCheckboxSelected = () => {
      selectedCheckBoxes.value = tableItems.map(item =>
        item[props.checkboxFieldToEmit].toString(),
      );
      emitSelectedCheckboxes();
    };

    const radioToggleSelected = (evt: { value: string; key: string }) => {
      emit('radioToggleSelected', evt);
    };

    /* eslint-disable consistent-return */
    const getColumnClass = (columnName: string, columnValue: any) => {
      if (
        props.clickableColumn !== undefined &&
        columnName === props.clickableColumn
      ) {
        // Check if the row is disabled for clickable column
        if (
          props.clickableDisabledField &&
          columnValue[props.clickableDisabledField]
        ) {
          return 'clickable-column clickable-column--disabled';
        }
        return 'clickable-column';
      }
      if (
        props.deleteColumn !== undefined &&
        columnName === props.deleteColumn
      ) {
        return 'deleteColumn';
      }
      if (props.editColumn !== undefined && columnName === props.editColumn) {
        return 'editColumn';
      }
      if (
        props.radioToggleColumn !== undefined &&
        columnName === props.radioToggleColumn
      ) {
        return 'radioToggleColumn';
      }
      if (props.copyColumn !== undefined && columnName === props.copyColumn) {
        return 'copy-column';
      }
      if (
        props.statusColumn &&
        columnName === props.statusColumn &&
        props.statusCategories
      ) {
        const status = columnValue[columnName as keyof {}];
        const baseStyle = props?.dropdownColumn
          ? 'status-pill-with-dropdown'
          : 'status-pill';

        switch (props.statusCategories.get(status)?.toLowerCase()) {
          case 'green':
            return `${baseStyle} status-pill__green`;
          case 'orange':
            return `${baseStyle} status-pill__orange`;
          case 'red':
            return `${baseStyle} status-pill__red`;
          default:
            return 'status-pill status-pill__default';
        }
      }
      if (
        props.checkboxColumn !== undefined &&
        columnName === props.checkboxColumn
      ) {
        return 'checkbox-column';
      }
    };

    return {
      tableItems,
      headers,
      columnNumber,
      openDropdown,
      selectedCheckBoxes,
      getColumnClass,
      columnAction,
      copyCell,
      radioToggleSelected,
      onCheckboxSelected,
      onHeaderCheckboxSelected,
      onHeaderCheckboxUnselected,
      onCheckboxUnselected,
    };
  },
});
</script>

<style lang="scss">
@use '../../assets/scss/include-media' as media;
@use '../../assets/scss/colors' as colors;

.checkbox-cell .checkbox-form {
  padding-top: 20px;

  & .item {
    & .checkmark {
      border-radius: 4px !important;
      border-width: 2px !important;
      border-color: colors.$brand-primary-yellow-400 !important;
      width: 16px !important;
      height: 16px !important;
    }

    &:hover {
      & input {
        & ~ .checkmark {
          background-color: colors.$brand-white !important;
        }
      }
    }

    & input {
      &:checked {
        & ~ .checkmark {
          background-color: colors.$brand-primary-yellow-400 !important;
        }
      }
    }
  }
}

table {
  border-collapse: collapse;
  border: 1px solid colors.$brand-grey-200;
  width: 100%;
  table-layout: inherit;
}

th,
td {
  padding: 23px;
  vertical-align: middle;
}

th:first-child,
td:first-child {
  border-right: 1px solid colors.$brand-grey-200;
}

th {
  text-align: left;
}

.wrapper {
  position: relative;
  overflow-x: scroll;
  white-space: nowrap;
}

.copy-icon {
  margin: 3px;
  padding-left: 5px;
  cursor: pointer;
}

.checkbox-column {
  text-align: center;
}

.status-container {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
}

.clickable-container {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
}

.wrapper table td:first-child {
  @include media.media('<=tablet') {
    position: sticky;
    left: 0;
    background: colors.$brand-white;
    border-right: 1px solid colors.$brand-grey-200;
  }
}

.wrapper table thead th:first-child {
  @include media.media('<=tablet') {
    position: sticky;
    left: 0;
    background: colors.$brand-grey-50;
    border-right: 1px solid colors.$brand-grey-200;
  }
}

.wrapper table tr:first-child {
  @include media.media('<=tablet') {
    position: sticky;
    left: 0;
    background: colors.$brand-white;
    border-right: 1px solid colors.$brand-grey-200;
  }
}

tr {
  border-bottom: 1px solid colors.$brand-grey-200;
  vertical-align: middle;
}

.header {
  box-sizing: border-box;
  padding: 16px 24px 16px 16px;
  gap: 8px;
  width: 60px;
  height: 44px;
  background: colors.$brand-grey-50;
  border: 1px solid colors.$brand-grey-200;
  border-radius: 8px 0 0 0;
  font-family: 'Jost', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: colors.$brand-grey-600;
}

.row {
  background: colors.$brand-white;
  font-family: 'Jost', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.clickable-column {
  text-align: center;
  color: #4fa58c;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &--disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.copy-column {
  text-align: center;
  line-height: 20px;

  > div {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    > span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}

.crypto-column {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 400;
  font-size: 14px;

  > img {
    width: 30%;
    margin: 5px;
  }
}

.drop-down-list {
  border: 1px solid colors.$brand-secondary-purple;
  padding-right: 10px;
  background-color: colors.$brand-white;
  border-radius: 5px;
  width: 5rem;
  height: 40px;
}

.dropdownColumn {
  gap: 4px;
  align-content: space-around;
  font-family: 'Jost', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: colors.$brand-secondary-purple;
  border: none !important;
  cursor: pointer;
  height: 100%;
  width: calc(110%);
  background-color: transparent !important;
  outline: none !important;
  padding-left: 10px;
}

.deleteSVG,
.editSVG {
  background: transparent;
  border: none;
  cursor: pointer;
}

.deleteColumn {
  border-left: 1px solid colors.$brand-grey-200;
  width: 10px;
}

.select-background {
  padding: 12px 8px 12px 16px;
  gap: 10px;
  width: 209px;
  height: 48px;
  background: colors.$brand-white;
}

.status-pill-with-dropdown {
  border-radius: 40px;
  font-weight: 500;
  width: fit-content;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.status-pill {
  border-radius: 40px;
  font-weight: 500;
  width: fit-content;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;

  &__green {
    color: colors.$brand-secondary-teal-500;
    background: colors.$brand-secondary-teal-25;
  }

  &__orange {
    color: colors.$brand-primary-yellow-600;
    background: colors.$brand-primary-yellow-25;
  }

  &__red {
    color: colors.$status-error;
    background: #ffe2e8;
  }

  &__default {
    color: #646363;
    background: #d3d1d1;
  }
}
</style>
